import { useState, useEffect } from 'react';

const TimeInput = ({ onChange, onValidationChange }: { value?: number; onChange?: (v: number) => void; onValidationChange?: (isValid: boolean) => void }) => {
    const [minutes, setMinutes] = useState<string>('');
    const [hours, setHours] = useState<string>('');
    const [hoursTouched, setHoursTouched] = useState<boolean>(false);
    const [minutesTouched, setMinutesTouched] = useState<boolean>(false);
    
    const isHoursValid = hours !== '' && !isNaN(parseInt(hours)) && parseInt(hours) >= 0 && parseInt(hours) <= 23;
    const isMinutesValid = minutes !== '' && !isNaN(parseInt(minutes)) && parseInt(minutes) >= 0 && parseInt(minutes) <= 59;
    
    const hoursError = hoursTouched && !isHoursValid;
    const minutesError = minutesTouched && !isMinutesValid;
    const isValid = isHoursValid && isMinutesValid;
    
    useEffect(() => {
        onValidationChange?.(isValid);
    }, [isValid, onValidationChange]);
    
    const onMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        
        // Allow only digits or empty, max 2 digits
        if (inputValue === '' || (/^\d+$/.test(inputValue) && inputValue.length <= 2)) {
            setMinutes(inputValue);
            
            // Update onChange
            if (inputValue !== '') {
                const num = parseInt(inputValue);
                if (!isNaN(num)) {
                    const hNum = hours === '' ? 0 : (parseInt(hours) || 0);
                    onChange?.(hNum * 100 + num);
                }
            } else {
                const hNum = hours === '' ? 0 : (parseInt(hours) || 0);
                onChange?.(hNum * 100);
            }
            
            // Auto-focus next field when 2 digits are entered
            if (inputValue.length === 2) {
                setTimeout(() => {
                    (e.target.nextElementSibling as HTMLInputElement)?.focus();
                }, 0);
            }
        }
    };
    
    const onHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        
        // Allow only digits or empty, max 2 digits
        if (inputValue === '' || (/^\d+$/.test(inputValue) && inputValue.length <= 2)) {
            setHours(inputValue);
            
            // Update onChange
            if (inputValue !== '') {
                const num = parseInt(inputValue);
                if (!isNaN(num)) {
                    const mNum = minutes === '' ? 0 : (parseInt(minutes) || 0);
                    onChange?.(num * 100 + mNum);
                }
            } else {
                const mNum = minutes === '' ? 0 : (parseInt(minutes) || 0);
                onChange?.(mNum);
            }
            
            // Auto-focus next field when 2 digits are entered
            if (inputValue.length === 2) {
                setTimeout(() => {
                    (e.target.nextElementSibling as HTMLInputElement)?.focus();
                }, 0);
            }
        }
    };
    
    const onMinuteBlur = () => {
        setMinutesTouched(true);
        if (minutes !== '') {
            const num = parseInt(minutes);
            if (!isNaN(num) && num >= 0 && num <= 59) {
                setMinutes(String(num).padStart(2, '0'));
            } else {
                setMinutes('');
            }
        }
    };
    
    const onHourBlur = () => {
        setHoursTouched(true);
        if (hours !== '') {
            const num = parseInt(hours);
            if (!isNaN(num) && num >= 0 && num <= 23) {
                setHours(String(num).padStart(2, '0'));
            } else {
                setHours('');
            }
        }
    };
    
    return (
        <div className='flex flex-col' dir='ltr'>
            <div className='flex flex-row gap-1'>
                <input 
                    className={`border-b flex flex-1 w-16 px-2 py-1 order-1 ${hoursError ? 'border-red-600 border-b-2 text-red-700 bg-red-50 placeholder-red-300' : 'border-gray-300'}`}
                    maxLength={2} 
                    placeholder='00' 
                    type='text' 
                    onChange={onHourChange}
                    onBlur={onHourBlur}
                    onFocus={() => setHoursTouched(true)}
                    value={hours} 
                />
                <input 
                    className={`border-b flex flex-1 w-16 px-2 py-1 order-2 ${minutesError ? 'border-red-600 border-b-2 text-red-700 bg-red-50 placeholder-red-300' : 'border-gray-300'}`}
                    maxLength={2} 
                    placeholder='00' 
                    type='text' 
                    onChange={onMinuteChange}
                    onBlur={onMinuteBlur}
                    onFocus={() => setMinutesTouched(true)}
                    value={minutes} 
                />
            </div>
            {(hoursError || minutesError) && (
                <div className='text-xs text-red-600 mt-1 font-semibold' dir='rtl'>
                    יש למלא שעה ודקות תקינים
                </div>
            )}
        </div>
    );
};

export default TimeInput;
